import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  type InputHTMLAttributes,
} from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label: string;
  /**
   * Keeps the label for screen readers but takes it off the screen — for a row
   * that already names the thing being ticked, like a student in a register.
   * The label must still say which box this is ("Select Aarav Sharma"), never
   * just "Select". Mirrors `Input`'s prop of the same name.
   */
  hideLabel?: boolean;
  /**
   * Neither ticked nor clear — "some of the rows below are selected".
   *
   * For a select-all box at the head of a list. It is a DOM property rather
   * than an attribute, so it can only be set through the element itself; that
   * is why this component keeps a ref of its own.
   */
  indeterminate?: boolean;
}

/**
 * Uses a real <input type="checkbox"> with `appearance-none` rather than a div,
 * so keyboard, form submission and screen readers all work for free.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, hideLabel = false, indeterminate = false, className, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  // The caller's ref still points at the input; this one exists so the
  // indeterminate property can be written after every render that changes it.
  const innerRef = useRef<HTMLInputElement>(null);
  useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);

  useEffect(() => {
    if (innerRef.current) innerRef.current.indeterminate = indeterminate;
  }, [indeterminate]);

  return (
    <div className="flex items-center gap-2">
      <div className="relative inline-flex items-center justify-center">
        <input
          ref={innerRef}
          id={inputId}
          type="checkbox"
          className={cn(
            'peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-[5px] border border-line-strong bg-surface',
            'transition-colors duration-150',
            'checked:border-accent checked:bg-accent',
            'indeterminate:border-accent indeterminate:bg-accent',
            'focus:outline-none focus:ring-2 focus:ring-accent/25',
            className,
          )}
          {...props}
        />
        <Check className="pointer-events-none absolute hidden h-3 w-3 text-white peer-checked:block stroke-[3]" />
        <Minus className="pointer-events-none absolute hidden h-3 w-3 text-white peer-indeterminate:block stroke-[3]" />
      </div>
      <label
        htmlFor={inputId}
        className={cn('cursor-pointer select-none text-sm text-ink-2', hideLabel && 'sr-only')}
      >
        {label}
      </label>
    </div>
  );
});
